import { string } from "./model";

export const SPACING: Record<string, string> = {
  none: "0px",
  xs: "4px",
  sm: "8px",
  md: "16px",
  lg: "32px",
  xl: "48px",
  "2xl": "96px",
};
const LENGTH = /^-?\d+(?:\.\d+)?(?:px|rem|em|vh|vw|svh|%)$/i;
const NON_NEGATIVE_LENGTH = /^\d+(?:\.\d+)?(?:px|rem|em|vh|vw|%)$/i;

export function isCssLength(value: unknown): value is string {
  return typeof value === "string" && LENGTH.test(value.trim());
}

/** One gap or distance: a spacing token or a non-negative CSS length; anything else is undefined (the CSS default). */
export function lengthOrToken(raw: unknown): string | undefined {
  const value = string(raw).trim();
  if (value in SPACING) return SPACING[value];
  return NON_NEGATIVE_LENGTH.test(value) ? value : undefined;
}

/** One gap per device, `mobile | desktop` (one part applies to both); an unreadable part is undefined. */
export function responsiveLength(raw: unknown): {
  mobile?: string;
  desktop?: string;
} {
  if (typeof raw !== "string") return {};
  const [mobile, desktop = mobile] = raw.split("|");
  return { mobile: lengthOrToken(mobile), desktop: lengthOrToken(desktop) };
}

/** 1–4 spacing tokens or non-negative CSS lengths in CSS shorthand order; anything else is undefined. */
export function lengthBox(raw: unknown): string | undefined {
  const values = string(raw).trim().split(/\s+/).map(lengthOrToken);
  return values.length <= 4 && values.every(Boolean)
    ? values.join(" ")
    : undefined;
}

/** 1–4 spacing tokens in CSS shorthand order; anything else gives `fallback`. */
export function spacingCss(raw: unknown, fallback = "0px"): string {
  const values = string(raw).trim().split(/\s+/);
  return values.length <= 4 && values.every((v) => v in SPACING)
    ? values.map((v) => SPACING[v]).join(" ")
    : fallback;
}

export interface BoxSides {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

/** One screen's box: 1–4 spacing tokens or CSS lengths, CSS shorthand order; anything else counts as 0. */
function boxSides(part: string): BoxSides | undefined {
  const tokens = part.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length || tokens.length > 4) return undefined;
  const css = tokens.map((token) =>
    token in SPACING ? SPACING[token] : isCssLength(token) ? token : "0px",
  );
  const [a, b = a, c = a, d = b] = css;
  return { top: a, right: b, bottom: c, left: d };
}

/** `mobile | desktop` boxes; a single box applies to both screens. */
export function boxCss(raw: unknown): {
  mobile?: BoxSides;
  desktop?: BoxSides;
} {
  if (typeof raw !== "string") return {};
  const parts = raw.split("|");
  const mobile = boxSides(parts[0] ?? "");
  const desktop = parts.length > 1 ? boxSides(parts[1] ?? "") : mobile;
  return { mobile, desktop };
}
